import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import headLogo from "../head/head.png";
import "./styles.css";

const API_URL = (process.env.REACT_APP_PHOTOS_API_URL || "").replace(/\/$/, "");
const MAX_FILES = 10;
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_IMAGE_EDGE = 2500;
const JPEG_QUALITY = 0.86;
const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

function errorMessage(error, fallback) {
  return error?.message || fallback;
}

async function apiRequest(path, invitationCode, options = {}) {
  if (!API_URL) {
    throw new Error("La galería todavía no está conectada al servicio de fotos.");
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "X-Invitation-Code": invitationCode,
      ...options.headers,
    },
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || "No fue posible completar la solicitud.");
  }

  return data;
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("No fue posible preparar la fotografía."));
        }
      },
      "image/jpeg",
      JPEG_QUALITY
    );
  });
}

async function preparePhoto(file) {
  if (!ALLOWED_TYPES.has(file.type)) {
    throw new Error(`${file.name}: usa una imagen JPEG, PNG o WebP.`);
  }
  if (file.size > MAX_FILE_BYTES) {
    throw new Error(`${file.name}: la imagen supera 20 MB.`);
  }

  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { alpha: false });
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await canvasToBlob(canvas);
  if (blob.size > MAX_FILE_BYTES) {
    throw new Error(`${file.name}: no fue posible reducirla por debajo de 20 MB.`);
  }

  const baseName = file.name.replace(/\.[^.]+$/, "") || "foto";
  return new File([blob], `${baseName}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

function uploadToS3({ uploadUrl, fields, file, onProgress }) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]) => formData.append(key, value));
    formData.append("file", file);

    request.open("POST", uploadUrl);
    request.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
    request.addEventListener("load", () => {
      if (request.status >= 200 && request.status < 300) {
        resolve();
      } else {
        reject(new Error("S3 rechazó la carga de la fotografía."));
      }
    });
    request.addEventListener("error", () => reject(new Error("Se interrumpió la carga de la fotografía.")));
    request.addEventListener("abort", () => reject(new Error("La carga fue cancelada.")));
    request.send(formData);
  });
}

function PhotoCard({ photo, onOpen }) {
  return (
    <article className="photo-card">
      <button type="button" onClick={onOpen} aria-label="Ampliar fotografía">
        <img src={photo.url} alt="Foto compartida por un invitado" loading="lazy" />
      </button>
    </article>
  );
}

export default function Photos() {
  const [codeInput, setCodeInput] = useState(() => sessionStorage.getItem("bodaPhotosCode") || "");
  const [invitationCode, setInvitationCode] = useState("");
  const [photos, setPhotos] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0, percent: 0 });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openPhotoIndex, setOpenPhotoIndex] = useState(null);
  const [status, setStatus] = useState({ type: "", message: "" });

  const closePhoto = useCallback(() => setOpenPhotoIndex(null), []);
  const showPreviousPhoto = useCallback(() => {
    setOpenPhotoIndex((current) => (current === null ? null : (current - 1 + photos.length) % photos.length));
  }, [photos.length]);
  const showNextPhoto = useCallback(() => {
    setOpenPhotoIndex((current) => (current === null ? null : (current + 1) % photos.length));
  }, [photos.length]);

  useEffect(() => {
    if (openPhotoIndex === null) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") closePhoto();
      if (event.key === "ArrowLeft" && photos.length > 1) showPreviousPhoto();
      if (event.key === "ArrowRight" && photos.length > 1) showNextPhoto();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [closePhoto, openPhotoIndex, photos.length, showNextPhoto, showPreviousPhoto]);

  const loadPhotos = useCallback(async (code) => {
    setLoading(true);
    setStatus({ type: "", message: "" });
    try {
      const data = await apiRequest("/photos", code);
      setPhotos(data.photos || []);
      setInvitationCode(code);
      sessionStorage.setItem("bodaPhotosCode", code);
    } catch (error) {
      setInvitationCode("");
      setPhotos([]);
      sessionStorage.removeItem("bodaPhotosCode");
      setStatus({ type: "error", message: errorMessage(error, "No fue posible abrir la galería.") });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const savedCode = sessionStorage.getItem("bodaPhotosCode");
    if (savedCode) {
      loadPhotos(savedCode);
    }
  }, [loadPhotos]);

  const handleAccess = (event) => {
    event.preventDefault();
    const code = codeInput.trim().toUpperCase();
    if (!code) {
      setStatus({ type: "error", message: "Ingresa tu código de invitación." });
      return;
    }
    loadPhotos(code);
  };

  const handleFiles = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length > MAX_FILES) {
      setStatus({ type: "error", message: `Selecciona un máximo de ${MAX_FILES} fotos por carga.` });
      event.target.value = "";
      return;
    }
    setSelectedFiles(files);
    setStatus({ type: "", message: "" });
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!selectedFiles.length) {
      setStatus({ type: "error", message: "Selecciona al menos una fotografía." });
      return;
    }

    setUploading(true);
    setStatus({ type: "", message: "" });
    setProgress({ current: 1, total: selectedFiles.length, percent: 0 });

    try {
      for (let index = 0; index < selectedFiles.length; index += 1) {
        setProgress({ current: index + 1, total: selectedFiles.length, percent: 0 });
        const file = await preparePhoto(selectedFiles[index]);
        const signed = await apiRequest("/uploads/presign", invitationCode, {
          method: "POST",
          body: JSON.stringify({ fileName: file.name, contentType: file.type, size: file.size }),
        });
        await uploadToS3({
          ...signed,
          file,
          onProgress: (percent) =>
            setProgress({ current: index + 1, total: selectedFiles.length, percent }),
        });
        await apiRequest("/photos", invitationCode, {
          method: "POST",
          body: JSON.stringify({ key: signed.key }),
        });
      }

      setSelectedFiles([]);
      setProgress({ current: 0, total: 0, percent: 0 });
      await loadPhotos(invitationCode);
      setStatus({ type: "success", message: "Tus fotografías ya están en la galería." });
    } catch (error) {
      setStatus({ type: "error", message: errorMessage(error, "No fue posible subir las fotografías.") });
    } finally {
      setUploading(false);
    }
  };

  return (
    <main className="photos-page">
      <section className="photos-hero">
        <img src={headLogo} alt="Boda Gálvez Barrios" className="photos-logo" />
        <p className="photos-eyebrow">NUESTROS MOMENTOS</p>
        <h1>Comparte tus fotos</h1>
        <p>Sube los recuerdos que captures y disfruta los momentos compartidos por los demás invitados.</p>
      </section>

      {!invitationCode ? (
        <section className="photos-access-card">
          <h2>Entrar a la galería</h2>
          <p>Utiliza el mismo código de tu invitación.</p>
          <form onSubmit={handleAccess}>
            <label htmlFor="photos-code">Código de invitación</label>
            <input
              id="photos-code"
              value={codeInput}
              onChange={(event) => setCodeInput(event.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())}
              autoComplete="off"
              disabled={loading}
              placeholder="Ingresa tu código"
            />
            <button type="submit" disabled={loading}>{loading ? "Validando..." : "Ver fotografías"}</button>
          </form>
          {status.message && <p className={`photos-status ${status.type}`}>{status.message}</p>}
          <Link to="/" className="photos-back-link">Volver a la invitación</Link>
        </section>
      ) : (
        <>
          <section className="photos-upload-card">
            <div>
              <p className="photos-eyebrow">GALERÍA COMPARTIDA</p>
              <h2>Agrega tus recuerdos</h2>
              <p>Puedes seleccionar hasta {MAX_FILES} fotos.</p>
            </div>
            <form onSubmit={handleUpload}>
              <label className="photos-file-picker">
                <span>{selectedFiles.length ? `${selectedFiles.length} foto(s) seleccionada(s)` : "Seleccionar fotografías"}</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  onChange={handleFiles}
                  disabled={uploading}
                />
              </label>
              {uploading && (
                <div className="photos-progress" aria-live="polite">
                  <div><span style={{ width: `${progress.percent}%` }} /></div>
                  <p>Subiendo foto {progress.current} de {progress.total}: {progress.percent}%</p>
                </div>
              )}
              <button type="submit" disabled={uploading || !selectedFiles.length}>
                {uploading ? "Compartiendo..." : "Compartir fotografías"}
              </button>
            </form>
            {status.message && <p className={`photos-status ${status.type}`}>{status.message}</p>}
          </section>

          <section className="photos-gallery-section">
            <div className="photos-gallery-heading">
              <div>
                <p className="photos-eyebrow">GALERÍA</p>
                <h2>Momentos compartidos</h2>
              </div>
              <button type="button" className="photos-refresh" onClick={() => loadPhotos(invitationCode)} disabled={loading}>
                {loading ? "Actualizando..." : "Actualizar"}
              </button>
            </div>
            {photos.length ? (
              <div className="photos-grid">
                {photos.map((photo, index) => (
                  <PhotoCard
                    key={photo.photoId}
                    photo={photo}
                    onOpen={() => setOpenPhotoIndex(index)}
                  />
                ))}
              </div>
            ) : (
              <div className="photos-empty">
                <h3>Aún no hay fotografías</h3>
                <p>Sé la primera persona en compartir un momento.</p>
              </div>
            )}
          </section>

          <Link to="/" className="photos-back-link photos-page-back">Volver a la invitación</Link>

          {openPhotoIndex !== null && photos[openPhotoIndex] && (
            <div className="photo-viewer" role="dialog" aria-modal="true" aria-label="Fotografía ampliada" onClick={closePhoto}>
              <button type="button" className="photo-viewer-close" onClick={closePhoto} aria-label="Cerrar fotografía">
                ×
              </button>
              {photos.length > 1 && (
                <button
                  type="button"
                  className="photo-viewer-navigation photo-viewer-previous"
                  onClick={(event) => { event.stopPropagation(); showPreviousPhoto(); }}
                  aria-label="Fotografía anterior"
                >
                  ‹
                </button>
              )}
              <img
                src={photos[openPhotoIndex].url}
                alt="Foto compartida por un invitado ampliada"
                onClick={(event) => event.stopPropagation()}
              />
              {photos.length > 1 && (
                <button
                  type="button"
                  className="photo-viewer-navigation photo-viewer-next"
                  onClick={(event) => { event.stopPropagation(); showNextPhoto(); }}
                  aria-label="Fotografía siguiente"
                >
                  ›
                </button>
              )}
              <span className="photo-viewer-count">{openPhotoIndex + 1} / {photos.length}</span>
            </div>
          )}
        </>
      )}
    </main>
  );
}
